import { Link } from '@tanstack/react-router';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { isAccountAttempt, type AuditEntry } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { ToneChip } from '../../../../components/Tone';
import { formatDateTime } from '../../../../lib/format';
import { changesOf, factsOf, headlineOf, kindLabelOf, toneOf, type AccessLogLookups } from './access-log-words';
import styles from './AccessLogDetail.module.css';

// USER-06's right column (Access log A): the clicked entry explained in full — what exactly changed, before → after,
// who and from where, and links to the person, everything they did, and the role. On a phone it is its own view.

interface AccessLogDetailProps {
  readonly entry: AuditEntry;
  readonly lookups: AccessLogLookups;
  /** On a phone: back to the feed. */
  readonly onBack?: () => void;
}

export function AccessLogDetail({ entry, lookups, onBack }: AccessLogDetailProps): ReactNode {
  const changes = changesOf(entry, lookups);
  const facts = factsOf(entry, lookups);
  const actorFirstName = entry.actorName?.split(' ')[0] ?? null;
  const showsHolders = changes.length > 0 && entry.roleHolderCount !== null && entry.roleLabel !== null && entry.eventType !== 'user.role_assigned';

  return (
    <article className={styles.detail} aria-labelledby="access-log-entry-title">
      {onBack && (
        <div className={styles.back}>
          <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>
            Access log
          </Button>
        </div>
      )}

      <header className={styles.head}>
        <ToneChip tone={toneOf(entry)}>{kindLabelOf(entry.category)}</ToneChip>
        <h2 id="access-log-entry-title" className={styles.title}>
          {headlineOf(entry)}
        </h2>
        <p className={styles.when}>{formatDateTime(entry.occurredAt)}</p>
      </header>

      {changes.length > 0 && (
        <section className={styles.section} aria-labelledby="access-log-changed">
          <h3 id="access-log-changed" className={styles.heading}>
            What changed
          </h3>
          <ul className={styles.changes}>
            {changes.map((change, index) => (
              <li key={`${change.label}-${index}`} className={styles.change}>
                <span className={styles.changeLabel}>{change.label}</span>
                <span className={styles.beforeAfter}>
                  <span className={styles.box}>
                    <span className={styles.boxTag}>Before</span>
                    {change.before}
                  </span>
                  <ArrowRight size={14} className={styles.arrow} aria-label="became" />
                  <span className={styles.box}>
                    <span className={styles.boxTag}>After</span>
                    {change.after}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {showsHolders && (
            <p className={styles.note}>
              Applies to the {entry.roleLabel} role: {entry.roleHolderCount} {entry.roleHolderCount === 1 ? 'person' : 'people'}.
            </p>
          )}
        </section>
      )}

      <section className={styles.section} aria-labelledby="access-log-who-where">
        <h3 id="access-log-who-where" className={styles.heading}>
          Who and where
        </h3>
        <dl className={styles.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={styles.fact}>
              <dt>{fact.label}</dt>
              <dd className={fact.isCode ? styles.code : undefined}>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {(entry.personId || entry.actorId || entry.roleId) && (
        <nav className={styles.links} aria-label="Go from this entry">
          {entry.roleId && entry.roleLabel && (
            <Link to="/admin/roles" search={{ role: entry.roleId }} className={styles.link}>
              Open the {entry.roleLabel} role
            </Link>
          )}
          {entry.personId && (
            <Link to="/admin/users/$userId" params={{ userId: entry.personId }} className={styles.link}>
              {entry.personName ? `Open ${entry.personName}'s page` : 'Open their page'}
            </Link>
          )}
          {/* Review R21: an attempt on their account is not something they did. */}
          {entry.actorId && !isAccountAttempt(entry.eventType) && (
            <Link to="/admin/audit" search={{ actorId: entry.actorId }} className={styles.link}>
              Everything by {actorFirstName ?? 'them'}
            </Link>
          )}
        </nav>
      )}
    </article>
  );
}
